import type { IPushBridge } from './ports/IPushBridge'
import type { IPushTopicStore } from './ports/IPushTopicStore'

/** Serialize native operations; a newer desired state supersedes queued work. */
export class SyncNewsTopics {
  #queue: Promise<void> = Promise.resolve()
  #revision = 0
  #confirmed = new Set<string>()

  constructor(private readonly bridge: IPushBridge, private readonly store: IPushTopicStore) {}

  execute(topics: string[], token?: string): Promise<void> {
    const revision = ++this.#revision
    const desired = [...new Set(topics)]
    const run = this.#queue.then(() => this.#sync(desired, token, revision))
    this.#queue = run.catch(() => {})
    return run
  }

  reset() {
    ++this.#revision
    this.#confirmed.clear()
    this.store.write({ token: '', topics: [], pending: [] })
  }

  async #sync(desired: string[], token: string | undefined, revision: number) {
    if (revision !== this.#revision || !this.bridge.canManageTopics()) return
    const current = token || (await this.bridge.getToken()).token
    if (revision !== this.#revision) return
    let state = this.store.read()
    if (state.token !== current) {
      this.#confirmed.clear()
      state = { token: current, topics: [], pending: [] }
      this.store.write(state)
    }
    // Remove the previous subdivision even if an earlier subscribe timed out.
    for (const topic of new Set([...state.topics, ...state.pending])) {
      if (desired.includes(topic)) continue
      this.#confirmed.delete(topic)
      await this.bridge.unsubscribeTopic(topic)
      if (revision !== this.#revision) return
      state.topics = state.topics.filter((t) => t !== topic)
      state.pending = state.pending.filter((t) => t !== topic)
      this.#confirmed.delete(topic)
      this.store.write(state)
    }
    for (const topic of desired) {
      if (revision !== this.#revision) return
      if (this.#confirmed.has(topic)) continue
      state.pending = [...new Set([...state.pending, topic])]
      this.store.write(state)
      await this.bridge.subscribeTopic(topic)
      if (revision !== this.#revision) return
      state.pending = state.pending.filter((t) => t !== topic)
      state.topics = [...new Set([...state.topics, topic])]
      this.#confirmed.add(topic)
      this.store.write(state)
    }
  }
}
